// File: client/src/components/analytics/DownloadDataButton.js
import React, { useState } from 'react';
import {
    Button,
    Dropdown,
    Menu,
    Modal,
    Checkbox,
    Radio,
    Space,
    Typography,
    Divider,
    message
} from 'antd';
import { DownloadOutlined, FileExcelOutlined, FileTextOutlined } from '@ant-design/icons';

const { Text } = Typography;

const DownloadDataButton = ({
                                data,
                                filteredData,
                                callListConfig,
                                fieldConfig,
                                embedded = false,     // New prop for embedded mode
                                onFinish
                            }) => {
    const [modalVisible, setModalVisible] = useState(embedded);
    const [exportFormat, setExportFormat] = useState('csv');
    const [useFiltered, setUseFiltered] = useState(true);
    const [exportMode, setExportMode] = useState('callList');
    const [isExporting, setIsExporting] = useState(false);

    const handleMenuClick = ({ key }) => {
        if (key === 'export') {
            setModalVisible(true);
        }
    };

    const handleModalClose = () => {
        setModalVisible(false);
        if (onFinish) onFinish();
    };

    // Handle download completion
    const handleDownloadComplete = () => {
        downloadData();
        if (onFinish) onFinish();
    };

    const downloadData = () => {
        try {
            setIsExporting(true);
            const dataToExport = useFiltered ? filteredData : data;

            if (!dataToExport || dataToExport.length === 0) {
                message.error('No data to export');
                setIsExporting(false);
                return;
            }

            let exportedData;
            let filename;
            let mimeType;

            if (exportMode === 'callList') {
                // Format the data according to call list columns, flags, and tags
                exportedData = formatCallListData(dataToExport);
            } else {
                // Export all raw data
                exportedData = dataToExport;
            }

            if (exportFormat === 'json') {
                // Export as JSON
                const jsonStr = JSON.stringify(exportedData, null, 2);
                const blob = new Blob([jsonStr], { type: 'application/json' });
                filename = `analytics_data_${new Date().toISOString().slice(0, 10)}.json`;
                mimeType = 'application/json';

                // Create a download link
                downloadBlob(blob, filename);
            } else {
                // Export as CSV
                const csvStr = convertToCSV(exportedData);
                const blob = new Blob([csvStr], { type: 'text/csv;charset=utf-8;' });
                filename = `analytics_data_${new Date().toISOString().slice(0, 10)}.csv`;
                mimeType = 'text/csv';

                // Create a download link
                downloadBlob(blob, filename);
            }

            message.success(`Data exported successfully as ${filename}`);
            setModalVisible(false);
        } catch (error) {
            console.error('Error exporting data:', error);
            message.error('Failed to export data');
        } finally {
            setIsExporting(false);
        }
    };

    const downloadBlob = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const formatCallListData = (rawData) => {
        return rawData.map(call => {
            const formattedCall = {
                id: call.id,
                date: call.date,
                time: call.rawDate ? new Date(call.rawDate).toLocaleTimeString() : '',
            };

            // Add columns from call list config
            if (callListConfig?.columns) {
                callListConfig.columns.forEach(column => {
                    const fieldName = column.field;
                    let value = call[fieldName];

                    // Apply any custom expression if defined
                    if (column.expression) {
                        try {
                            const fn = new Function('record', 'value', `return ${column.expression}`);
                            value = fn(call, value);
                        } catch (error) {
                            console.error(`Error evaluating expression for ${fieldName}:`, error);
                        }
                    }

                    formattedCall[column.label || fieldName] = value;
                });
            }

            // Add flags as boolean columns
            if (callListConfig?.flags) {
                callListConfig.flags.forEach(flag => {
                    try {
                        const matchesCondition = new Function('record', `return ${flag.condition}`)(call);
                        formattedCall[`Flag: ${flag.label}`] = matchesCondition;
                    } catch (error) {
                        console.error(`Error evaluating flag condition for ${flag.label}:`, error);
                        formattedCall[`Flag: ${flag.label}`] = false;
                    }
                });
            }

            // Add tags
            if (callListConfig?.tags) {
                callListConfig.tags.forEach(tag => {
                    const fieldName = tag.field;
                    let value = call[fieldName];

                    // Apply any custom expression if defined
                    if (tag.expression) {
                        try {
                            const fn = new Function('record', 'value', `return ${tag.expression}`);
                            value = fn(call, value);
                        } catch (error) {
                            console.error(`Error evaluating expression for tag ${fieldName}:`, error);
                        }
                    }

                    formattedCall[`Tag: ${tag.label}`] = value;
                });
            }

            return formattedCall;
        });
    };

    const convertToCSV = (data) => {
        if (!data || data.length === 0) return '';

        // Extract all unique keys from the data
        const allKeys = new Set();
        data.forEach(item => {
            Object.keys(item).forEach(key => allKeys.add(key));
        });

        const headers = Array.from(allKeys);

        // Create CSV header row
        let csv = headers.map(key => `"${String(key).replace(/"/g, '""')}"`).join(',') + '\n';

        // Add data rows
        data.forEach(item => {
            const row = headers.map(key => {
                const value = item[key];

                // Handle different value types
                if (value === null || value === undefined) {
                    return '';
                } else if (typeof value === 'string') {
                    return `"${value.replace(/"/g, '""')}"`;
                } else if (typeof value === 'boolean') {
                    return value ? 'true' : 'false';
                } else if (value instanceof Date) {
                    return `"${value.toISOString()}"`;
                } else {
                    return `"${String(value).replace(/"/g, '""')}"`;
                }
            }).join(',');

            csv += row + '\n';
        });

        return csv;
    };

    const menu = (
        <Menu onClick={handleMenuClick}>
            <Menu.Item key="export" icon={<DownloadOutlined />}>
                Export Data
            </Menu.Item>
        </Menu>
    );

    const getDataCountText = () => {
        if (!data) return '';
        return useFiltered
            ? `${filteredData?.length || 0} of ${data?.length} records will be exported.`
            : `All ${data?.length} records will be exported.`;
    };

    return (
        <>
            {!embedded && (
            <Button
                type="link"
                icon={<DownloadOutlined />}
                onClick={() => setModalVisible(true)}
                style={{ height: '32px', padding: '4px 15px' }}
            >
                Export Data
            </Button>
            )}

            <Modal
                title="Export Analytics Data"
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={downloadData}
                footer={[
                    <Button key="cancel" onClick={handleModalClose}>
                        Cancel
                    </Button>,
                    <Button
                        key="download"
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleDownloadComplete}
                        loading={isExporting}
                    >
                        Download
                    </Button>
                ]}
                okText="Download"
                confirmLoading={isExporting}
                width={520}
                bodyStyle={{ padding: '24px' }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Export Format Section */}
                    <div>
                        <Text style={{ fontSize: '16px', fontWeight: 500, display: 'block', marginBottom: '16px', color: '#111827' }}>
                            Export Format
                        </Text>
                        <Radio.Group
                            value={exportFormat}
                            onChange={e => setExportFormat(e.target.value)}
                            style={{ display: 'flex', gap: '16px' }}
                        >
                            <Radio.Button value="csv" style={{ height: '40px', display: 'flex', alignItems: 'center', paddingRight: '16px' }}>
                                <Space align="center">
                                    <FileExcelOutlined style={{ fontSize: '16px', color: '#10b981' }}/>
                                    <span>CSV</span>
                                </Space>
                            </Radio.Button>
                            <Radio.Button value="json" style={{ height: '40px', display: 'flex', alignItems: 'center', paddingRight: '16px' }}>
                                <Space align="center">
                                    <FileTextOutlined style={{ fontSize: '16px', color: '#3b82f6' }}/>
                                    <span>JSON</span>
                                </Space>
                            </Radio.Button>
                        </Radio.Group>
                    </div>

                    <Divider style={{ margin: '0' }} />

                    {/* Data Selection Section */}
                    <div>
                        <Text style={{ fontSize: '16px', fontWeight: 500, display: 'block', marginBottom: '16px', color: '#111827' }}>
                            Data Selection
                        </Text>
                        <div
                            onClick={() => setUseFiltered(!useFiltered)}
                            style={{
                                padding: '16px',
                                borderRadius: '8px',
                                background: '#f9fafb',
                                border: '1px solid #e5e7eb',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox
                                    checked={useFiltered}
                                    onChange={e => setUseFiltered(e.target.checked)}
                                    style={{ fontSize: '15px' }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    Export only filtered data
                                </Checkbox>
                            </div>
                            <div style={{
                                marginTop: '8px',
                                color: '#6b7280',
                                fontSize: '14px',
                                padding: '8px 12px',
                                background: '#f3f4f6',
                                borderRadius: '6px'
                            }}>
                                {getDataCountText()}
                            </div>
                        </div>
                    </div>

                    <Divider style={{ margin: '0' }} />

                    {/* Export Mode Section */}
                    <div>
                        <Text style={{ fontSize: '16px', fontWeight: 500, display: 'block', marginBottom: '16px', color: '#111827' }}>
                            Export Mode
                        </Text>
                        <Radio.Group
                            value={exportMode}
                            onChange={e => setExportMode(e.target.value)}
                            style={{ width: '100%' }}
                        >
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                {/* Call List Format Option */}
                                <div
                                    onClick={() => setExportMode('callList')}
                                    style={{
                                        padding: '16px',
                                        borderRadius: '8px',
                                        background: exportMode === 'callList' ? '#f0fdf4' : '#f9fafb',
                                        border: `1px solid ${exportMode === 'callList' ? '#86efac' : '#e5e7eb'}`,
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'flex-start'
                                    }}
                                >
                                    <Radio
                                        value="callList"
                                        checked={exportMode === 'callList'}
                                        onClick={e => e.stopPropagation()}
                                        style={{ padding: '0', marginRight: '12px', marginTop: '2px' }}
                                    />

                                    <div style={{ flex: 1 }}>
                                        <Text strong style={{ fontSize: '15px' }}>Call List Format</Text>
                                        <div style={{
                                            color: '#4b5563',
                                            fontSize: '14px',
                                            marginTop: '8px',
                                            lineHeight: '1.5'
                                        }}>
                                            Format data using columns, flags, and tags as defined in your call list view
                                        </div>
                                    </div>
                                </div>

                                {/* All Fields Option */}
                                <div
                                    onClick={() => setExportMode('raw')}
                                    style={{
                                        padding: '16px',
                                        borderRadius: '8px',
                                        background: exportMode === 'raw' ? '#f0fdf4' : '#f9fafb',
                                        border: `1px solid ${exportMode === 'raw' ? '#86efac' : '#e5e7eb'}`,
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'flex-start'
                                    }}
                                >
                                    <Radio
                                        value="raw"
                                        checked={exportMode === 'raw'}
                                        onClick={e => e.stopPropagation()}
                                        style={{ padding: '0', marginRight: '12px', marginTop: '2px' }}
                                    />

                                    <div style={{ flex: 1 }}>
                                        <Text strong style={{ fontSize: '15px' }}>All Fields</Text>
                                        <div style={{
                                            color: '#4b5563',
                                            fontSize: '14px',
                                            marginTop: '8px',
                                            lineHeight: '1.5'
                                        }}>
                                            Include all available data fields
                                        </div>
                                    </div>
                                </div>
                            </Space>
                        </Radio.Group>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default DownloadDataButton;
